<template>
  <!-- The shell, search row, options, arrow-key navigation and footer are TwnPaletteDialog's. What stays
       here is the command center's own: barcode injection, ⌘K, workflow modes and Enter-to-open a match.

       Two modes drive one dialog. Commands, and stock mode, which useStockMode owns end to end. This
       component only chooses which of the two each prop reads, so the dialog never closes and reopens
       between them and the search box keeps its focus. -->
  <TwnPaletteDialog
    ref="palette"
    v-model:query="searchQuery"
    :block-enter="isEnterBlocked"
    :can-go-back="isStockMode"
    :empty-message="emptyMessage"
    :filter-locally="isStockFilteredLocally"
    :hints="activeHints"
    :input-attrs="searchInputAttrs"
    label="Command center"
    :placeholder="activePlaceholder"
    :sections="sections"
    :select-only-match="false"
    :visible="isOpen"
    @back="stepBackInStockMode"
    @enter="onEnter"
    @select="onRowSelected"
    @update:visible="onVisibleChange"
  >
    <!-- Stock mode's breadcrumb, beside a Back button: the way out a merchant can see.

         A click on Back keeps focus in the search box, where scans and typing land. Focus is not moved back
         after a keyboard press instead: that Enter's keyup would reach the box and could confirm a save. -->
    <template
      v-if="isStockMode"
      #search-leading
    >
      <TwnBaseBtn
        aria-label="Back"
        icon="arrow_back"
        severity="secondary"
        size="small"
        variant="text"
        @click="stepBackInStockMode"
        @mousedown.prevent
      />
      <span
        class="min-w-0 max-w-48 truncate rounded-md bg-surface-100 px-2 py-0.5 text-sm font-medium text-surface-700"
      >
        {{ stockCrumb }}
      </span>
    </template>

    <template #search-trailing>
      <TwnKbd size="md">⌘+K</TwnKbd>
    </template>

    <!-- Scope pills — which of the four searchable things this query asks. A user sees only the
         pills their permissions allow, and the last one on cannot be switched off.

         A toolbar with a roving tabindex, which is what makes it reachable by keyboard without
         fighting the list: Tab from the input lands on one pill, Left/Right move between them,
         Space or Enter toggles, Tab again moves on. TwnPaletteDialog owns Up/Down for the rows, and
         stopping propagation here keeps the two sets of keys from ever meeting. -->
    <template #filters>
      <!-- In stock mode the pills give way to the location: a stock change is about one shelf. -->
      <TwnStockLocationChip
        v-if="isStockMode"
        :location="stockLocation"
        @change="openStockLocationsFromChip"
      />
      <div
        v-else-if="hasSeveralScopes"
        aria-label="Search scopes"
        class="flex flex-wrap items-center gap-2 border-b border-surface-200 px-4 py-3"
        role="toolbar"
        @keydown="onScopeKeydown"
      >
        <button
          v-for="scope in availableScopes"
          :key="scope.id"
          :ref="element => setScopeRef(scope.id, element)"
          :aria-pressed="isScopeActive(scope.id)"
          :class="scopePillClass(scope.id)"
          :tabindex="scope.id === focusedScopeId ? 0 : -1"
          type="button"
          @click="toggleScope(scope.id)"
          @focus="focusedScopeId = scope.id"
        >
          <MaterialIcon :ligature="scope.icon" />
          {{ scope.label }}
        </button>
      </div>

      <!-- Stock mode's save and status lines are rows the box never points at, so these hidden regions say them.
           They stay in the page, empty outside stock mode, so a line present as stock mode opens is still heard.
           The save comes first, so a landed save is heard before the next hint. -->
      <p
        class="sr-only"
        role="status"
      >
        {{ stockSaveStatus }}
      </p>
      <p
        :id="stockStatusId"
        class="sr-only"
        role="status"
      >
        {{ stockStatusLine }}
      </p>
      <p
        class="sr-only"
        role="alert"
      >
        {{ stockSaveError }}
      </p>
    </template>

    <!-- Trailing chip — a product's matched SKU or its barcodes, or a document's state. A colour-and-icon
         cue that never truncates, and never shows a voided invoice in the success colour.

         A product row leads with the pack tag and the stock badge the products table draws. The
         badge is a bare TwnIndicator, not TwnStockLevel: the row is itself a button, and a
         breakdown button inside it would nest one button in another. -->
    <template #option-trailing="{ row }">
      <TwnStockRowTrailing
        v-if="row.data?.stock"
        :location-name="stockLocationName"
        :row="row.data.stock"
      />
      <TwnPackTag v-if="row.data?.isPack" />
      <TwnIndicator
        v-if="row.data?.stockBadge"
        :label="row.data.stockBadge.label"
        :severity="row.data.stockBadge.severity"
      />
      <span
        v-if="row.data?.badge"
        :class="badgeToneClass(row.data?.badgeTone)"
        class="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium"
      >
        <MaterialIcon
          v-if="row.data?.skuMatch"
          ligature="barcode"
          size="0.875rem"
        />
        <span class="tabular-nums">{{ row.data?.badge }}</span>
      </span>
    </template>
  </TwnPaletteDialog>
</template>

<script lang="ts" setup>
import { useMagicKeys, whenever } from '@vueuse/core'
import { type ComponentPublicInstance, computed, ref, useId, useTemplateRef, watch } from 'vue'

import type { TwnPaletteKeyHintsItemData } from '@/components/molecules/palette/twn-palette-key-hints/TwnPaletteKeyHints.types'
import type {
  TwnPaletteDialogRowData,
  TwnPaletteDialogSectionData
} from '@/components/organisms/twn-palette-dialog/TwnPaletteDialog.types'
import type { BadgeTone } from '@/domains/command-center/services/searchScopes'
import type { SearchScopeId } from '@/domains/command-center/services/searchService.types'
import type { BaseCommandMenuItem, IconTone, MenuItem } from '@/domains/command-center/types'

import TwnIndicator from '@/components/atoms/badges/twn-indicator/TwnIndicator.vue'
import TwnBaseBtn from '@/components/atoms/buttons/twn-base-btn/TwnBaseBtn.vue'
import MaterialIcon from '@/components/atoms/icons/material-icon/MaterialIcon.vue'
import TwnKbd from '@/components/atoms/typography/twn-kbd/TwnKbd.vue'
import { PALETTE_DEFAULT_HINTS } from '@/components/organisms/twn-palette-dialog/TwnPaletteDialog.consts'
import TwnPaletteDialog from '@/components/organisms/twn-palette-dialog/TwnPaletteDialog.vue'
import {
  injectedBarcode,
  injectedSearchResults,
  isInjectionInFlight,
  notifyBarcodeConsumed,
  setScanBlockedBarcode
} from '@/composables/use-barcode-scanner/useBarcodeInjector'
import { SCAN_BLOCKED_ROW_ID } from '@/domains/command-center/commandGenerator'
import TwnStockLocationChip from '@/domains/command-center/components/molecules/twn-stock-location-chip/TwnStockLocationChip.vue'
import TwnStockRowTrailing from '@/domains/command-center/components/molecules/twn-stock-row-trailing/TwnStockRowTrailing.vue'
import { useCommandCenterActions } from '@/domains/command-center/composables/use-command-center/useCommandCenter'
import { useCommandPalette } from '@/domains/command-center/composables/use-command-palette/useCommandPalette'
import { useStockMode } from '@/domains/command-center/composables/use-stock-mode/useStockMode'
import { isEnterStockMode } from '@/domains/command-center/handlers/stock/stockCommands'
import {
  PALETTE_PILL_ACTIVE_CLASS,
  PALETTE_PILL_CLASS,
  PALETTE_PILL_IDLE_CLASS
} from '@/domains/command-center/types/consts/PalettePill.consts'
import TwnPackTag from '@/domains/products/components/atoms/twn-pack-tag/TwnPackTag.vue'

import type { CommandPaletteDialogSectionData } from './CommandPaletteDialog.types'

// Soft, tokenless accent tints cycled per section — mirrors the reference's
// colourful icon badges while staying visually intentional.
const BADGE_COLORS = [
  'bg-blue-100 text-blue-600',
  'bg-violet-100 text-violet-600',
  'bg-emerald-100 text-emerald-600',
  'bg-amber-100 text-amber-600',
  'bg-rose-100 text-rose-600',
  'bg-cyan-100 text-cyan-600'
] as const

const FALLBACK_ROW_ICON = 'bolt'

/** The trailing chip's palette. A state the registry does not name reads neutral, never green. */
const BADGE_TONE_CLASSES: Record<BadgeTone, string> = {
  success: 'bg-emerald-100 text-emerald-700',
  warn: 'bg-amber-100 text-amber-700',
  danger: 'bg-red-100 text-red-700',
  neutral: 'bg-surface-100 text-surface-600'
}

const badgeToneClass = (tone: BadgeTone | undefined) => BADGE_TONE_CLASSES[tone ?? 'neutral']

/** The leading icon's tones, from the theme's own green: the trailing chip's emerald predates it. */
const ICON_TONE_CLASSES: Record<IconTone, string> = {
  success: 'bg-green-100 text-green-700'
}

/** A row that names its own tone, such as a saved change, keeps it. Every other row takes its section's tint. */
const iconBadgeClass = (item: BaseCommandMenuItem, sectionIndex: number) =>
  item.iconTone ? ICON_TONE_CLASSES[item.iconTone] : BADGE_COLORS[sectionIndex % BADGE_COLORS.length]

// Lets a barcode scan type into the search box instead of being swallowed as a shortcut.
const BARCODE_PASSTHROUGH = { 'data-barcode-passthrough': '' } as const

const { isOpen, open, close, toggle } = useCommandPalette()

const searchQuery = ref<string | undefined>()

const palette = useTemplateRef<{ focus: () => void }>('palette')

const {
  isActive: isStockMode,
  isSaving: isStockSaving,
  menuItems: stockMenuItems,
  placeholder: stockPlaceholder,
  hints: stockHints,
  crumb: stockCrumb,
  location: stockLocation,
  isFilteredLocally: isStockStepFilteredLocally,
  statusLine: stockStatusLine,
  saveStatus: stockSaveStatus,
  saveError: stockSaveError,
  enter: enterStockMode,
  exit: exitStockMode,
  select: selectStockRow,
  confirm: confirmStockRow,
  back: stepBackInStockMode,
  openLocations: openStockLocations,
  receiveScan: receiveStockScan
} = useStockMode(searchQuery)

const {
  loading,
  menuItems,
  executeCommand,
  dynamicPlaceholder,
  workflowStateMode,
  availableScopes,
  isScopeActive,
  toggleScope,
  resetScopes,
  searchScannedBarcode
} = useCommandCenterActions(searchQuery, { isSuspended: isStockMode })

const keys = useMagicKeys()
const commandK = keys['Meta+K']
const controlK = keys['Ctrl+K']

// Roving tabindex: exactly one pill is tabbable, so Tab enters and leaves the row in one press
// rather than stepping through every scope.
const scopeElements = new Map<SearchScopeId, HTMLButtonElement>()

const setScopeRef = (scopeId: SearchScopeId, element: Element | ComponentPublicInstance | null) => {
  if (element instanceof HTMLButtonElement) {
    scopeElements.set(scopeId, element)

    return
  }

  scopeElements.delete(scopeId)
}

/** Tab lands on the first pill until the user moves, and on whichever they left after that. */
const focusedScopeId = ref<SearchScopeId | undefined>()

watch(
  availableScopes,
  scopes => {
    const stillThere = scopes.some(scope => scope.id === focusedScopeId.value)
    if (!stillThere) focusedScopeId.value = scopes[0]?.id
  },
  { immediate: true }
)

const focusScopeAt = (index: number) => {
  const scopes = availableScopes.value
  if (!scopes.length) return

  const target = scopes[(index + scopes.length) % scopes.length]
  focusedScopeId.value = target.id
  scopeElements.get(target.id)?.focus()
}

/**
 * Left and Right only. Up and Down belong to TwnPaletteDialog's row list, and stopping propagation
 * here keeps the two sets of keys from meeting. Space and Enter need no handler — a focused
 * `<button>` already fires its click.
 */
const onScopeKeydown = (event: KeyboardEvent) => {
  const currentIndex = availableScopes.value.findIndex(scope => scope.id === focusedScopeId.value)

  const move: Record<string, number | undefined> = {
    ArrowRight: currentIndex + 1,
    ArrowLeft: currentIndex - 1,
    Home: 0,
    End: availableScopes.value.length - 1
  }

  const nextIndex = move[event.key]
  if (nextIndex === undefined) return

  event.preventDefault()
  event.stopPropagation()
  focusScopeAt(nextIndex)
}

const scopePillClass = (scopeId: SearchScopeId) => [
  PALETTE_PILL_CLASS,
  isScopeActive(scopeId) ? PALETTE_PILL_ACTIVE_CLASS : PALETTE_PILL_IDLE_CLASS
]

/** The pills, and the Tab hint, only earn their place once there is more than one scope to move between. */
const hasSeveralScopes = computed(() => availableScopes.value.length > 1)

const hints = computed<readonly TwnPaletteKeyHintsItemData[]>(() =>
  hasSeveralScopes.value ? [...PALETTE_DEFAULT_HINTS, { keys: ['Tab'], label: 'Filters' }] : PALETTE_DEFAULT_HINTS
)

// Each prop the dialog reads comes from whichever mode is active.
const activeHints = computed(() => (isStockMode.value ? stockHints.value : hints.value))

const activePlaceholder = computed(() => (isStockMode.value ? stockPlaceholder.value : dynamicPlaceholder.value))

const activeMenuItems = computed<MenuItem[]>(() => (isStockMode.value ? stockMenuItems.value : menuItems.value))

const isEnterBlocked = computed(() => (isStockMode.value ? isStockSaving.value : loading.value))

const isStockFilteredLocally = computed(() => isStockMode.value && isStockStepFilteredLocally.value)

// Stock mode empties only when the merchant's text filters out every row of a step, so it names that text.
const emptyMessage = computed(() =>
  isStockMode.value
    ? `Nothing matches "${searchQuery.value?.trim() ?? ''}". Clear the search to see them all.`
    : 'Type to search'
)

const stockLocationName = computed(() => stockLocation.value?.name ?? '')

const stockStatusId = useId()

// In stock mode the step's status line describes the box, so "How many?" is never all a screen reader hears.
const searchInputAttrs = computed(() =>
  isStockMode.value ? { ...BARCODE_PASSTHROUGH, 'aria-describedby': stockStatusId } : BARCODE_PASSTHROUGH
)

// A loose, disabled row is a status indicator (Searching… / No results): drawn as text, not an option.
// So is a row that says it is one, wherever it sits: stock mode puts its hint under the item's heading.
const toRow = (item: MenuItem, sectionIndex: number, isLoose: boolean): TwnPaletteDialogRowData<MenuItem> => ({
  key: (item.id as string) ?? (item.label as string),
  label: item.label as string,
  subLabel: item.subLabel as string | undefined,
  icon: (item.icon as string) || FALLBACK_ROW_ICON,
  disabled: Boolean(item.disabled),
  badgeClass: iconBadgeClass(item as BaseCommandMenuItem, sectionIndex),
  isStatus: (item as BaseCommandMenuItem).isStatus === true || (isLoose && Boolean(item.disabled)),
  data: item
})

// An entry with `items` is a group even when it is empty, so an empty one draws nothing rather than a dead option.
const isGroup = (item: MenuItem): boolean => Array.isArray(item.items)

// Normalise the menu (groups with `items`, plus loose rows) into flat sections for rendering. Consecutive loose
// rows share one section.
const sections = computed<TwnPaletteDialogSectionData<MenuItem>[]>(() => {
  const entries = activeMenuItems.value

  // Command mode surfaces its loose indicators (Searching… / No exact match) above the command groups. Stock
  // mode keeps the order its builders wrote: a hint after the reasons, the last save above the next search.
  const ordered = isStockMode.value
    ? entries
    : [...entries.filter(item => !isGroup(item)), ...entries.filter(item => isGroup(item))]

  const built: CommandPaletteDialogSectionData[] = []

  ordered.forEach((item, index) => {
    if (isGroup(item)) {
      const label = item.label as string | undefined
      built.push({ key: label ?? `section-${index}`, label, items: item.items as MenuItem[], isLoose: false })

      return
    }

    const previous = built.at(-1)
    if (previous?.isLoose) previous.items.push(item)
    else built.push({ key: `section-loose-${index}`, items: [item], isLoose: true })
  })

  return built.map((section, sectionIndex) => ({
    key: section.key,
    label: section.label,
    rows: section.items.map(item => toRow(item, sectionIndex, section.isLoose))
  }))
})

/** Scans and typing land in the search box only while it holds focus, so stock mode hands focus back to it. */
const refocusSearch = () => palette.value?.focus()

// A row is a button that only a pointer can focus, and the next step unmounts it, so focus would fall to the page.
const onStockRowSelected = (item: BaseCommandMenuItem | MenuItem) => {
  // A refused select, such as a scanner's Enter on a row the pointer rests on, leaves the palette as it was.
  if (!selectStockRow(item)) return

  if (item.onSuccess === 'closeMenu') {
    close()

    return
  }

  refocusSearch()
}

// The location list is navigated from the box. An Enter's keyup landing there in the location step saves nothing.
const openStockLocationsFromChip = () => {
  openStockLocations()
  refocusSearch()
}

const onItemSelected = (item: BaseCommandMenuItem | MenuItem) => {
  if (isStockMode.value) {
    onStockRowSelected(item)

    return
  }

  // The blocked-scan row IS the fix it names: selecting it switches Products on and re-runs the
  // search, rather than closing the palette on a row that carried no command.
  if (item.id === SCAN_BLOCKED_ROW_ID) {
    searchScannedBarcode()

    return
  }

  // Show feedback to the user when selecting a parent product mid-workflow.
  if (workflowStateMode.value === 'awaitingParentProduct') {
    item.onSelect = 'showToast'
  }

  // A stock command hands the palette over to stock mode, which this component owns. Only into a palette
  // still open: the command answers after a moment, and the merchant may have closed it by then. A failing
  // command is left to surface as it always has, rather than swallowed here.
  void executeCommand(item).then(result => {
    if (isEnterStockMode(result) && isOpen.value) enterStockMode(result)
  })

  searchQuery.value = undefined
  notifyBarcodeConsumed()

  if (item.onSuccess === 'closeMenu') close()
}

const onRowSelected = (row: TwnPaletteDialogRowData<MenuItem>) => {
  if (row.data) onItemSelected(row.data)
}

// Nothing highlighted: fall back to scan Enter-to-navigate. Only a UNIQUE perfect
// match auto-opens (mirrors useBarcodeAutoForward); ambiguous multi-product matches
// never pick one arbitrarily, and a scanner's trailing Enter (fired before results
// load, with no highlight) stays inert. A highlighted row is TwnPaletteDialog's to open.
const onEnterWithoutHighlight = (query: string | undefined) => {
  const flatItems = menuItems.value.flatMap(group => group.items ?? [group])
  const perfectMatches = flatItems.filter((item: MenuItem) => {
    const baseItem = item as BaseCommandMenuItem
    return baseItem.skuMatch?.hasMatch === true && baseItem.skuMatch.matchedSku?.toLowerCase() === query?.toLowerCase()
  })

  if (perfectMatches.length === 1) onItemSelected(perfectMatches[0] as BaseCommandMenuItem)
}

const onEnter = (query: string | undefined) => {
  if (isStockMode.value) {
    confirmStockRow()

    return
  }

  onEnterWithoutHighlight(query)
}

const onVisibleChange = (visible: boolean) => {
  if (!visible) close()
}

whenever(commandK, toggle)
whenever(controlK, toggle)

// Reset barcodes when the palette closes; TwnPaletteDialog focuses the search as it opens.
watch(isOpen, opened => {
  if (opened) return

  // Nothing is staged in stock mode, so closing loses nothing. It reopens on the commands.
  exitStockMode()
  searchQuery.value = undefined
  notifyBarcodeConsumed()
  // The palette opens the same way every time, so a closed one forgets which pills were toggled.
  resetScopes()
  setScanBlockedBarcode(undefined)
})

// When the user manually clears the input, reset accumulated barcodes so the
// next scan starts fresh instead of restoring old barcodes. Stock mode clears the box on every
// step, so it spends each scan itself instead.
watch(searchQuery, newValue => {
  if (isStockMode.value) return

  if ((!newValue || newValue.trim() === '') && !isInjectionInFlight.value) {
    notifyBarcodeConsumed()
  }
})

// A scanned barcode auto-opens the palette so injected results are always visible. In stock mode
// the scan is an item to count, not a query, so stock mode takes it.
watch(injectedBarcode, barcode => {
  if (barcode === undefined) return
  open()

  if (isStockMode.value) {
    receiveStockScan(barcode, injectedSearchResults.value)

    return
  }

  searchQuery.value = barcode
})
</script>
